import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, FileText, Receipt, Search, Users, Wrench } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { client: Users, vehicle: Car, repair_order: Wrench, quote: FileText, invoice: Receipt };
const LABELS = { client: "Client", vehicle: "Véhicule", repair_order: "Dossier", quote: "Devis", invoice: "Facture" };

export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const box = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => { setResults(r.data); setOpen(true); setActive(0); }).catch(() => setResults([])), 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        box.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (r) => {
    setOpen(false);
    setQ("");
    navigate(r.path);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
    if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative w-full max-w-xl">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={box}
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder="Rechercher : nom, téléphone, plaque, VIN, n° devis ou facture…  (Ctrl+K)"
        className="h-10 w-full rounded-full border border-border bg-secondary/60 pl-10 pr-4 text-sm outline-none transition-[border-color,box-shadow] focus:border-primary/60 focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl" data-testid="global-search-results">
          {results.map((r, i) => {
            const Icon = ICONS[r.type] || Search;
            return (
              <button key={`${r.type}-${r.id}`} onMouseDown={() => go(r)} data-testid={`search-result-${r.type}-${i}`} className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${i === active ? "bg-primary/10" : "hover:bg-muted/60"}`}>
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{r.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{r.subtitle}</div>
                </div>
                <span className="eyebrow">{LABELS[r.type]}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
